import styled from 'styled-components';
import type { ReactNode } from 'react';
import { s8, s16, zIndex } from '@ardoq/design-tokens';
import { connect } from '@ardoq/rxbeach';
import { map } from 'rxjs';
import {
  isI18nMessage,
  translate,
  type I18nLocale,
  type I18nMessage,
} from '@ardoq/i18n';
import { Toast, type ToastCardProps } from './Toast';
import { AnimatedToast } from './AnimatedToast';
import { closeToast, toast$ } from './toast$AndRoutine';
import type { StackedToast, ToastActionButton, ToastPhase } from './types';

// This + padding top makes sure toast appear in exactly 128px from the top, padding makes the animation smoother
export const TOAST_OFFSET_TOP = 112;

export const ToastsContainer = styled.div`
  position: fixed;
  display: flex;
  flex-direction: column;
  right: ${s16};
  padding-top: ${s8};
  z-index: ${zIndex.TOAST};
  align-items: flex-end;
  pointer-events: none; /* to avoid blocking user interaction */
`;

type ToastViewProps = ToastCardProps & {
  id: number;
  phase: ToastPhase;
  maxWidth?: 'fit-content';
};

const translateIfMessage = (
  text: string | I18nMessage | ReactNode,
  i18nLocale: I18nLocale
): ReactNode => {
  if (!isI18nMessage(text)) return text;
  return translate(i18nLocale, text);
};

const toActionButtonProps = (
  { label, onClick }: ToastActionButton,
  i18nLocale: I18nLocale,
  close: VoidFunction
): ToastCardProps['actionButton'] => ({
  label: translateIfMessage(label, i18nLocale),
  onClick: () => {
    onClick();
    close();
  },
});

const toToastViewProps = (
  { id, phase, type, message, actionButton, maxWidth }: StackedToast,
  i18nLocale: I18nLocale
): ToastViewProps => {
  const close = () => closeToast(id);
  return {
    id,
    phase,
    type,
    maxWidth,
    message: translateIfMessage(message, i18nLocale),
    actionButton:
      actionButton && toActionButtonProps(actionButton, i18nLocale, close),
    onClose: close,
  };
};

type ToastStackCombinedProps = {
  offsetTop?: number;
  i18nLocale: I18nLocale;
  toasts: StackedToast[];
};

const ToastStackContent = ({
  offsetTop = TOAST_OFFSET_TOP,
  i18nLocale,
  toasts,
}: ToastStackCombinedProps) => {
  if (toasts.length === 0) return null;
  return (
    <ToastsContainer style={{ top: offsetTop }}>
      {toasts
        .map(toast => toToastViewProps(toast, i18nLocale))
        .map(({ id, phase, ...toastProps }) => (
          <AnimatedToast key={id} phase={phase}>
            <Toast {...toastProps} />
          </AnimatedToast>
        ))}
    </ToastsContainer>
  );
};

export const ToastStack = connect(
  ToastStackContent,
  toast$.pipe(map(({ toasts }) => ({ toasts })))
);
